import isArray from 'lodash/isArray';
import isNil from 'lodash/isNil';
import {window} from 'ssr-window';
import FESegmentUtils from '@spothero/utils/segment';
import ExperimentUtils from './experiment';
import EnvironmentUtils from '@spothero/utils/environment';
import UAParser from 'ua-parser-js';

/** Structures platform data for use in properties
* @static
* @function withDevice
* @example
* withDevice()
* @returns {Object} [{}] - platform object to merge into properties
*/
const withDevice = () => {
    const userAgent = navigator && navigator.userAgent;
    const uaParsed = userAgent && new UAParser(userAgent);
    const {
        browser,
        os,
    } = (uaParsed) ? uaParsed.getResult() : {};
    const {
        innerHeight: height,
        innerWidth: width,
    } = window;
    const isMobile = EnvironmentUtils.isMobileDevice();

    return {
        'device platform': (isMobile)
            ? 'mobile web'
            : 'desktop web',
        'device operating system': os && os.name,
        browser: browser && browser.name,
        'screen dimensions': {
            height,
            width,
        },
    };
};

/** Structures optimizely data for use in additionalOptions
* @static
* @function withOptimizely
* @example
* withOptimizely()
* @returns {Object} [{}] - Optimizely object to merge into additionalOptions
*/
const withOptimizely = () => {
    return {
        Optimizely: {
            userId: ExperimentUtils.getId()
        }
    };
};

const enhanceData = data => {
    const newData = (isArray(data))
        ? data
        : [data];

    return newData.map(item => ({
        ...item,
        additionalOptions: {
            ...item.additionalOptions,
            ...withOptimizely()
        },
        properties: {
            ...item.properties,
            ...withDevice(),
        },
    }));
};

/**
 * Utilities for working with Segment.
 * @module SegmentUtils
 */
const SegmentUtils = {
    /**
     * Sends page view data to Segment.
     * @static
     * @function track
     * @param {Object} [data={}] - The data to send to segment.
     * @param {String} data.name - The page name.
     * @param {Object} [data.properties=null] - The page properties object. See the properties object information <a href="https://segment.com/docs/spec/page/#properties" target="_blank" rel="noopener noreferrer">here</a> for details.
     * @param {Object} [data.additionalOptions=null] - Additional options to send with the event.
     * @example
     * SegmentUtils.page({
     *     name: 'Web Landing',
     *     properties: {
     *          referrer: referrer || document.referrer,
     *          'referrer affiliate': referrerAffiliate
     *     }
     * });
     * @returns {void}
     */
    page(data) {
        enhanceData(data).forEach(FESegmentUtils.page);
    },
    /**
     * Sends tracking event data to Segment.
     * @static
     * @function track
     * @param {Object} [data={}] - The data to send to segment.
     * @param {String} data.event - The event name.
     * @param {Object} [data.properties=null] - The event properties object. See the properties object information <a href="https://segment.com/docs/spec/track/#properties" target="_blank" rel="noopener noreferrer">here</a> for details.
     * @param {Object} [data.additionalOptions=null] - Additional options to send with the event.
     * @example
     * SegmentUtils.track({
     *     event: 'Searched',
     *     properties: {
     *         city: 'Chicago'
     *     }
     * });
     * @returns {void}
     */
    track(data) {
        enhanceData(data).forEach(FESegmentUtils.track);
    },

    /**
     * Allows consumers of the util to call the segment reset.
     * @static
     * @function reset
     * @example
     * SegmentUtils.reset();
     * @returns {void}
     */
    reset() {
        if (isNil(window.analytics)) { return null; }

        window.analytics.reset();
    }
};

export default {
    ...FESegmentUtils,
    ...SegmentUtils
};
